import React, { useCallback, useMemo, useState } from 'react'
import type { CommentDTO } from '../../api/comments'
import { useComments, type UseCommentsTarget } from '../../hooks/useComments'
import CommentItem from './CommentItem'
import CommentComposer from './CommentComposer'
import GiftSheet from '../gifts/GiftSheet'
import { useGiftCatalog } from '../../hooks/useGifts'
import { sendCommentGift } from '../../api/gifts'
import ReportModal from '../moderation/ReportModal'
import { newIdempotencyKey } from '../../lib/idempotency'

type Props = {
  open: boolean
  onClose: () => void
  target: UseCommentsTarget
}

export default function CommentsDrawer({ open, onClose, target }: Props) {
  const [sort, setSort] = useState<'top' | 'newest' | 'following'>('top')
  const { items, loading, canLoadMore, loadMore, create, react } = useComments(target, sort)

  const { items: catalog } = useGiftCatalog()

  const { parents, repliesByParent } = useMemo(() => {
    const parents: CommentDTO[] = []
    const replies = new Map<string | number, CommentDTO[]>()
    items.forEach((c) => {
      if (c.parent_id) {
        const list = replies.get(c.parent_id) ?? []
        list.push(c)
        replies.set(c.parent_id, list)
      } else {
        parents.push(c)
      }
    })
    replies.forEach((list, key) => {
      list.sort((a, b) => {
        const at = a.created_at ? new Date(a.created_at).getTime() : 0
        const bt = b.created_at ? new Date(b.created_at).getTime() : 0
        return bt - at
      })
      replies.set(key, list)
    })
    return { parents, repliesByParent: replies }
  }, [items])

  const [expanded, setExpanded] = useState<Set<string | number>>(new Set())
  const toggleExpanded = useCallback((id: string | number) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const [replyTo, setReplyTo] = useState<CommentDTO | null>(null)

  const [giftTarget, setGiftTarget] = useState<CommentDTO | null>(null)
  const closeGift = useCallback(() => setGiftTarget(null), [])
  const handleGiftSend = useCallback(async ({ gift }: { gift: any }) => {
    if (!giftTarget) return
    try {
      await sendCommentGift(giftTarget.id, { gift_id: gift.id, idempotency_key: newIdempotencyKey('giftc') })
    } catch (e: any) {
      window.alert(e?.response?.data?.detail || e?.response?.data?.message || e?.message || 'Gift failed')
    }
    setGiftTarget(null)
  }, [giftTarget])
  const [reportTarget, setReportTarget] = useState<CommentDTO | null>(null)
  const closeReport = useCallback(() => setReportTarget(null), [])

  return (
    <div aria-hidden={!open}>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 transition-opacity ${open ? 'opacity-100' : 'pointer-events-none opacity-0'} z-40`}
        onClick={onClose}
      />
      {/* Drawer */}
      <div
        role="dialog"
        aria-modal="true"
        className={[
          // Mobile: bottom sheet. Desktop: right drawer.
          'fixed z-50 transform transition-transform bg-neutral-900 shadow-2xl border-white/10',
          'left-0 right-0 bottom-0 top-auto w-full h-[min(78vh,640px)] border-t rounded-t-2xl',
          'md:left-auto md:top-0 md:bottom-auto md:right-0 md:h-full md:w-[360px] md:rounded-none md:border-l md:border-t-0',
          open ? 'translate-x-0 translate-y-0' : 'translate-y-full md:translate-y-0 md:translate-x-full',
        ].join(' ')}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-neutral-900 border-b border-white/10 px-3 py-2">
          <div className="flex items-center gap-2">
            <div className="text-base font-extrabold">Comments</div>
            <div className="ml-1 px-2 py-0.5 rounded-full text-xs bg-white/5 border border-white/10">{items.length}</div>
            <button onClick={onClose} className="ml-auto text-white/80 hover:text-white">X</button>
          </div>
          {items.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              {(['top', 'newest', 'following'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSort(s)}
                  className={`px-2.5 py-1 rounded-full border text-xs ${
                    sort === s ? 'bg-fuchsia-900/30 border-fuchsia-700/50 text-fuchsia-200' : 'bg-white/5 border-white/10 text-white/80'
                  }`}
                >
                  {s === 'top' ? 'Top' : s === 'newest' ? 'Newest' : 'Following'}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* List */}
        <div className="chat-scroll overflow-y-auto h-[calc(100%-148px)]">
          {parents.length === 0 && !loading ? (
            <div className="p-6 text-center text-white/80">
              <div className="text-2xl mb-2">💬</div>
              <div className="font-semibold">No comments yet</div>
              <div className="text-sm text-white/70">Be the first to start the conversation.</div>
            </div>
          ) : null}
          {parents.map((item) => {
            const child = repliesByParent.get(item.id) ?? []
            const isOpen = expanded.has(item.id)
            return (
              <div key={String(item.id)} className="border-b border-white/5">
                <CommentItem
                  comment={item}
                  onLike={(id) => react(id, 'like')}
                  onReply={(c) => setReplyTo(c)}
                  onGift={() => setGiftTarget(item)}
                  onReport={(c) => setReportTarget(c)}
                />
                {child.length > 0 && !isOpen && (
                  <button
                    onClick={() => toggleExpanded(item.id)}
                    className="ml-14 mb-3 px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-white/80"
                  >
                    View {child.length} repl{child.length === 1 ? 'y' : 'ies'}
                  </button>
                )}
                {isOpen && (
                  <div className="ml-11">
                    {child.map((r) => (
                      <CommentItem
                        key={String(r.id)}
                        comment={r}
                        onLike={(id) => react(id, 'like')}
                        onReply={(c) => setReplyTo(c)}
                        onGift={() => setGiftTarget(r)}
                        onReport={(c) => setReportTarget(c)}
                      />
                    ))}
                    <button
                      onClick={() => toggleExpanded(item.id)}
                      className="ml-3 mb-3 px-3 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-white/80"
                    >
                      Show less
                    </button>
                  </div>
                )}
              </div>
            )
          })}
          {canLoadMore && (
            <div className="p-3 flex items-center justify-center">
              <button onClick={loadMore} className="text-white/80 hover:text-white text-sm">Load more</button>
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="sticky bottom-0 bg-neutral-900 pb-[calc(0.25rem+env(safe-area-inset-bottom))]">
          <CommentComposer
            replyTo={replyTo ? { id: replyTo.id, name: replyTo.author?.name ?? replyTo.author?.handle ?? null } : null}
            onCancelReply={() => setReplyTo(null)}
            onSubmit={async (body, parentId) => {
              await create(body, parentId)
              if (parentId) setExpanded((s) => new Set(s).add(parentId))
              setReplyTo(null)
            }}
          />
        </div>
      </div>

      {/* Gift Sheet on comments */}
      <GiftSheet
        visible={Boolean(giftTarget)}
        onClose={closeGift}
        creatorName={giftTarget?.author?.name}
        avatarUrl={giftTarget?.author?.avatar_url ?? undefined}
        catalog={catalog}
        onSend={handleGiftSend}
      />
      <ReportModal
        open={Boolean(reportTarget)}
        onClose={closeReport}
        contentType="comment"
        contentId={reportTarget ? String(reportTarget.id) : ''}
        title={reportTarget?.author?.handle ? `Report @${reportTarget.author.handle}` : 'Report comment'}
      />
    </div>
  )
}

